import { startCase } from 'lodash';
import * as z from 'zod';

import {
  BuydownType,
  FhaAdpCode,
  FundingChannel,
  LoanInvestorName,
  PaymentFrequency,
  SpecialLoanStatus,
} from '../../loan/Loan';
import { LoanStatusActive } from '../../loan/LoanStatus';
import { OriginationData } from '../../loan/OriginationData';
import { OptionalPhoneNumber } from '../../Phone';
import { OptionalEmailString } from '../email';
import {
  AddressLine,
  Checkbox,
  CountryCode,
  dateIsAtMostThreeDaysInFuture,
  dateIsWithinRange,
  DateString,
  InterestRate,
  MonetaryValue,
  NonNegativeInteger,
  NonNegativeMonetaryValue,
  NonNegativePercentage,
  PositiveFloat,
  PositivePercentage,
  StateAbbreviation,
  Zipcode,
} from '../fields';
import { CreateLoanFields, CreateLoanMultipleAddressFields } from './createLoanFields';
import { InvestorSplit } from './RecordDrawDisbursementRow';
import { AddressFields } from './sharedFields/AddressFields';
import { LoanFeeOverrideFields } from './sharedFields/LoanFeeOverrides';
import { MERSCreateFields } from './sharedFields/MERSImportFields';

// Reference these inputs in client, not preprocessed types below
export const AmortizationTypeInput = z.enum(['arm', 'fixed']);
export const LoanTypeInput = z.enum(['arm', 'fixed', 'heloc']);
export const EscrowWaivedInput = z.enum(['Waived', 'Not Waived']);
export const InterestTypeInput = z.enum(['30-360', 'actual-365', 'actual-360']);
export const ProductTypeInput = z.enum([
  'fha',
  'va',
  'usda',
  'pih',
  'conv',
  'heloc',
  'bridge', // FOR VONTIVE
  'businessPurpose', // FOR VONTIVE
  'jumbo',
]);
export const LoanPurposeInput = z.enum(['purchase', 'refinance']);
export const OccupancyTypeInput = z.enum(['primary', 'second', 'investment']);
export const LoanStatusInput = z.enum([LoanStatusActive.value]);

// These are only for use in this file
export const LoanTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}, LoanTypeInput);
export const AmortizationTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}, AmortizationTypeInput);
export const EscrowWaivedEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return startCase(val.toLowerCase());
  }
  return val;
}, EscrowWaivedInput);
export const InterestTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}, InterestTypeInput);

export const ProductTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    if (val.toLowerCase() === 'businesspurpose') {
      return 'businessPurpose';
    }
    return val.toLowerCase();
  }
  return val;
}, ProductTypeInput);

export const LoanPurposeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}, LoanPurposeInput);

export const OccupancyTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}, OccupancyTypeInput);

export const ConstructionToPermanentStatusInput = z.enum(['active', 'permanent']);

export const ConstructionToPermanentStatusEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toLowerCase();
  }
  return val;
}, ConstructionToPermanentStatusInput);

export const ConstructionLoanTypeInput = z.enum(['constructionOnly', 'constructionToPermanent']);

export const ConstructionLoanTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    if (val.toLowerCase() === 'constructiononly') {
      return 'constructionOnly';
    }
    if (val.toLowerCase() === 'constructiontopermanent') {
      return 'constructionToPermanent';
    }
  }
  return val;
}, ConstructionLoanTypeInput);

export const FhaAdpCodeInput = FhaAdpCode;
export const FhaAdpCodeEnum = z.preprocess((val) => {
  if (typeof val === 'number') {
    return `${val}`;
  }
  if (typeof val === 'string') {
    return val.trim();
  }
  return val;
}, FhaAdpCodeInput);

export const FundingChannelInput = FundingChannel;
export const FundingChannelEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    const lower = val.toLowerCase().replace(/[-_ ]/g, '');
    if (lower === 'minicorrespondent') {
      return 'miniCorrespondent';
    }
    return lower;
  }
  return val;
}, FundingChannelInput);

export const SpecialLoanStatusInput = SpecialLoanStatus;
export const SpecialLoanStatusEnum = z.preprocess((val) => {
  if (typeof val === 'number') {
    return `${val}`;
  }
  if (typeof val === 'string') {
    return val.toUpperCase();
  }
  return val;
}, SpecialLoanStatusInput);

export const InvestorNameEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    val = val.toLowerCase().replace(/[_ ]/g, '');
  }
  switch (val) {
    case 'fnma':
    case 'fanniemae':
      return LoanInvestorName.enum.FNMA;
    case 'fhlmc':
    case 'freddiemac':
      return LoanInvestorName.enum.FHLMC;
    case 'gnma':
    case 'ginniemae':
      return LoanInvestorName.enum.GNMA;
    default:
      return val;
  }
}, LoanInvestorName);

export const NoteholderInput = z.enum(['investor', 'servicer', 'thirdParty']);
export const NoteholderEnum = z.preprocess((val) => {
  if (typeof val === 'number') {
    val = `${val}`;
  }
  if (typeof val === 'string') {
    val = val.toLowerCase();
  }

  switch (val) {
    case '0':
      return 'investor';
    case '1':
      return 'servicer';
    case '2':
    case 'thirdparty':
      return 'thirdParty';
    default:
      return val;
  }
}, NoteholderInput);

export const PaymentFrequencyEnum = z.preprocess((val) => {
  if (typeof val === 'number') {
    val = `${val}`;
  }
  if (typeof val === 'string') {
    val = val.toLowerCase();
  }

  switch (val) {
    case '1':
    case 'monthly':
      return 1;
    case '12':
    case 'annual':
      return 12;
    default:
      return val;
  }
}, PaymentFrequency);

// Flat escrow-agent / policy columns (escrow{1..3}*). Retained for the flatfile /
// account-upload path; the documented public API surface uses the nested
// `address{n}Insurance` form, so these are omitted from the API docs (see
// leaf-api's loan documentation).
export const EscrowAgentFields = z.object({
  escrow1Type: z.string().optional(),
  escrow1AgentName: z.string().optional(),
  escrow1AgentEmail: OptionalEmailString,
  escrow1AgentPhone: OptionalPhoneNumber,
  escrow1CompanyName: z.string().optional(),
  escrow1CompanyAddressLine1: AddressLine.optional(),
  escrow1CompanyAddressLine2: AddressLine.optional(),
  escrow1CompanyAddressCity: z.string().optional(),
  escrow1CompanyAddressState: StateAbbreviation.optional().or(z.literal('')),
  escrow1CompanyAddressZip: Zipcode.optional(),
  escrow1PolicyNumber: z.string().optional(),
  escrow1PolicyStart: DateString.optional(),
  escrow1PolicyEnd: DateString.optional(),
  escrow2Type: z.string().optional(),
  escrow2AgentName: z.string().optional(),
  escrow2AgentEmail: OptionalEmailString,
  escrow2AgentPhone: OptionalPhoneNumber,
  escrow2CompanyName: z.string().optional(),
  escrow2CompanyAddressLine1: AddressLine.optional(),
  escrow2CompanyAddressLine2: AddressLine.optional(),
  escrow2CompanyAddressCity: z.string().optional(),
  escrow2CompanyAddressState: StateAbbreviation.optional().or(z.literal('')),
  escrow2CompanyAddressZip: Zipcode.optional(),
  escrow2PolicyNumber: z.string().optional(),
  escrow2PolicyStart: DateString.optional(),
  escrow2PolicyEnd: DateString.optional(),
  escrow3Type: z.string().optional(),
  escrow3AgentName: z.string().optional(),
  escrow3AgentEmail: OptionalEmailString,
  escrow3AgentPhone: OptionalPhoneNumber,
  escrow3CompanyName: z.string().optional(),
  escrow3CompanyAddressLine1: AddressLine.optional(),
  escrow3CompanyAddressLine2: AddressLine.optional(),
  escrow3CompanyAddressCity: z.string().optional(),
  escrow3CompanyAddressState: StateAbbreviation.optional().or(z.literal('')),
  escrow3CompanyAddressZip: Zipcode.optional(),
  escrow3PolicyNumber: z.string().optional(),
  escrow3PolicyStart: DateString.optional(),
  escrow3PolicyEnd: DateString.optional(),
});

// NOTE: If parsing data with CreateLoanRow, make sure
// you trim and strip the data beforehand with the `trimAndStrip` util
export const CreateLoanRow = z
  .object({
    loanId: z.string(),
    poolId: z.string().optional(),
    previousServicerId: z.string().optional(),
    originatorId: z.string().optional(),
    capitalPartnerId: z.string().optional(),
    fullId: z.string().optional(),
    otherId1: z.string().optional(),
    otherId2: z.string().optional(),
    otherId3: z.string().optional(),
    ownerId: z
      .string()
      .optional()
      .describe(
        'Use this field when the owner is NOT Fannie Mae, Freddie Mac, or Ginnie Mae. To declare those owners, use `investorName`.',
      ),

    pointOfContact: OptionalEmailString.describe('Email address of internal user who is the primary point of contact'),

    fundingDate: DateString.and(dateIsAtMostThreeDaysInFuture),
    isAcquired: Checkbox.optional(),
    acquisitionDate: DateString.optional(),

    brokerName: z.string().optional(),

    previousLoanOwner: z.string().optional().describe('Name of the company that sold the loan to the current lender'),
    correspondentLender: z.string().optional().describe('Originating lender information'),
    correspondentLenderPhone: OptionalPhoneNumber.describe('Originating lender information'),
    correspondentLenderEmail: OptionalEmailString,

    loanMaturityDate: DateString,
    loanClosingDate: DateString.optional().describe('The date the loan closed'),
    incomingTransferEffectiveDate: DateString.optional(),
    loanTermMonths: NonNegativeInteger.optional(),
    loanTermYears: NonNegativeInteger.optional(),
    paymentFrequency: PaymentFrequencyEnum.optional(),
    manualStatementGeneration: Checkbox.optional().meta({
      description:
        'If true, statements will not be automatically generated and a user will need to generate the statements on a monthly basis',
      default: false,
    }),
    loanType: LoanTypeEnum.optional().meta({
      description: 'This field is deprecated. Use amortizationType instead.',
      deprecated: true,
    }),
    amortizationType: AmortizationTypeEnum.optional(),
    productType: ProductTypeEnum.optional(),
    productDescription: z.string().optional(),
    loanPurpose: LoanPurposeEnum.optional(),
    constructionToPermanentStatus: ConstructionToPermanentStatusEnum.optional().meta({
      description: 'Indicates if loan is in "active" construction or "permanent" status',
      default: 'active',
    }),
    constructionLoanType: ConstructionLoanTypeEnum.optional(),
    occupancyType: OccupancyTypeEnum.optional(),
    initialDiscountPointsAmount: MonetaryValue.optional().describe('Amount paid to purchase points'),
    ytdInterestPaid: NonNegativeMonetaryValue.optional(),
    ytdMortgageInsurancePaid: NonNegativeMonetaryValue.optional(),
    legalDescription: z.string().optional(),
    numberOfProperties: NonNegativeInteger.optional().describe(
      'Number of properties securing the mortgage. Used for 1098 tax forms',
    ),
    create1098Form: Checkbox.optional().meta({
      description: 'Default: true. Set to false to prevent 1098 from generating at year end',
      default: true,
    }),
    includeInCreditReport: Checkbox.optional().meta({
      description: 'Default: true. Set to false to exclude loan from Metro2® credit report',
      default: true,
    }),
    loanPrincipal: NonNegativeMonetaryValue.describe(
      'Starting principal balance. This value should exclude principal from initial draws provided with the request. For a loan modified before boarding, this is the modified principal, not the true original; credit reporting uses fundedAtClosingPrincipal for the original balance.',
    ),
    loanInterestRateDecimal: InterestRate.optional().describe(
      'Must include either: loanInterestRateDecimal OR loanInterestRatePercent',
    ),
    loanInterestRatePercent: NonNegativePercentage.optional().describe(
      'Must include either: loanInterestRateDecimal OR loanInterestRatePercent',
    ),
    firstCollectedPaymentDate: DateString.optional().describe(
      'Use this field if you are creating a loan with a firstPaymentDate in the past.\nThis is the date from which Willow will begin billing the consumer.\nIf you are retro-actively boarding a loan and you would like to collect all payments from the first payment date, simply set this to the same value as firstPaymentDate. \nIf you are boarding a loan that was repurchased, set this to the first date that you want to begin collecting payments.',
    ),
    dateOfFirstDelinquency: DateString.optional().describe(
      'Required only when the loan is 30+ days past due at boarding (a `firstCollectedPaymentDate` more than 30 days in the past) and investor or credit reporting is enabled. Date the loan first became delinquent under the prior servicer.',
    ),
    minMonthlyPayment: NonNegativeMonetaryValue.optional(),
    currentOutstandingAmount: NonNegativeMonetaryValue.optional().describe(
      "Use this field if you are creating a loan with a firstPaymentDate in the past.\nIf any payments were already collected at the time of boarding, Willow must know the current outstanding principal balance of the loan.\nIf no payments were collected, simply set this to the same value as the loanPrincipal.\nConstruction and draw-enabled loans: omit this field or set it equal to loanPrincipal. Set loanPrincipal to the outstanding principal at boarding, not including draws that haven't been billed yet, and provide those draws as draw1-draw3.",
    ),
    gracePeriod: NonNegativeInteger.optional().meta({
      description:
        'The number of days that a loan can be past-due before the loan is considered late. Default: Global company setting, or 15 days.',
      example: 15,
    }),
    omitFromDefaultInterestAutomation: Checkbox.optional(),
    defaultInterestRateDecimal: PositiveFloat.optional(),
    defaultInterestRatePercent: PositivePercentage.optional(),
    defaultInterestEffectiveDate: DateString.optional(),
    defaultInterestRemittanceSplit: InvestorSplit.optional().describe(
      'If not provided, the default interest will be remitted 100% to the servicer',
    ),
    lienPosition: NonNegativeInteger.optional(),
    noteDate: DateString.optional(),
    qualifiedMortgage: Checkbox.optional(),
    isAssumable: Checkbox.optional(),
    fundingChannel: FundingChannelEnum.optional(),
    specialLoanStatus: SpecialLoanStatusEnum.optional(),
    completionCertFlag: Checkbox.optional(),
    completionCertNotes: z.string().optional(),

    // investor info
    investorName: InvestorNameEnum.optional().describe(
      'Use this field when the investor is Fannie Mae, Freddie Mac, or Ginnie Mae. For any other investor, use `ownerId`.',
    ),
    investorLoanId: z.string().optional(),
    fhaCaseNumber: z.string().optional(),
    fhaAdpCode: FhaAdpCodeEnum.optional(),
    mers: MERSCreateFields.optional(),
    noteholder: NoteholderEnum.optional(),
    documentCustodian: z.string().optional(),

    // Renovo fields
    originalNoteHolder: z.string().optional(),
    servicerOrgId: z.string().optional(),
    subServicerOrgId: z.string().optional(),
    investorOrgId: z.string().optional(),
    ppc1OrgId: z.string().optional(),

    // stop settings
    stopEmails: Checkbox.optional().meta({
      description: 'If true, emails will not be sent to the borrowers on the loan',
      example: true,
      default: false,
    }),
    stopPayments: Checkbox.optional().meta({
      description: 'If true, payments will not be accepted for the loan',
      example: true,
      default: false,
    }),
    exactPaymentsOnly: Checkbox.optional(),
    omitFromPayoffAutomation: Checkbox.optional().meta({
      description:
        'If true, a payoff will not be automatically created upon borrower request. Note: Requires the company loan control setting to be enabled',
      example: true,
      default: false,
    }),
    omitFromDelinquentInterestAutomation: Checkbox.optional().meta({
      description: 'If true, default interest will not be automatically applied to the loan.',
      example: true,
      default: false,
    }),
    omitFromLateFeeAutomation: Checkbox.optional().meta({
      description:
        'If true, late fees will not be automatically applied to the loan. Note: defaults to true for loans with status bankruptcy, liquidation, or reo.',
      example: true,
      default: false,
    }),
    omitFromInterestOnEscrow: Checkbox.optional().meta({
      description: 'If true, the loan will be omitted from the interest on escrow report.',
      example: true,
      default: false,
    }),
    miDisbursementsPaused: Checkbox.optional().meta({
      description: 'If true, mortgage insurance disbursements will be paused for the loan.',
      example: true,
      default: false,
    }),

    // interest only fields
    interestOnlyTermMonths: NonNegativeInteger.optional(),
    interestOnlyTermYears: NonNegativeInteger.optional(),
    interestOnlyFlag: Checkbox.optional(),
    interestOnlyEndDate: DateString.optional(),

    // balloon fields
    balloonFlag: Checkbox.optional().describe(
      'If true, payments amortize over `balloonAmortizationMonths`/`balloonAmortizationYears` while the loan matures at `loanTermMonths`/`loanTermYears`, with the remaining principal due at maturity.',
    ),
    balloonAmortizationMonths: NonNegativeInteger.optional(),
    balloonAmortizationYears: NonNegativeInteger.optional(),

    // THIS IS AN OLD TODO: make `interestType` required but first need to reach out to loansnap
    interestType: InterestTypeEnum.optional(),
    firstPaymentPrincipal: NonNegativeMonetaryValue.optional().describe(
      'Must include either: firstPaymentPrincipalAndInterest OR firstPaymentPrincipal AND firstPaymentInterest',
    ),
    firstPaymentInterest: NonNegativeMonetaryValue.optional().describe(
      'Must include either: firstPaymentPrincipalAndInterest OR firstPaymentPrincipal AND firstPaymentInterest',
    ),
    firstPaymentPrincipalAndInterest: NonNegativeMonetaryValue.optional().describe(
      'Must include either: firstPaymentPrincipalAndInterest OR firstPaymentPrincipal AND firstPaymentInterest',
    ),
    firstPaymentTotal: NonNegativeMonetaryValue.optional().describe('P&I payment + escrow payment'),
    isEscrowed: Checkbox.optional(),
    escrowWaived: EscrowWaivedEnum.optional(),
    monthlyPaymentEscrow: NonNegativeMonetaryValue.optional().describe(
      'Note: This amount should include MI if applicable',
    ),
    initialEscrowAnalysisDate: DateString.optional().describe(
      'Date of the loan’s most recent escrow analysis in the prior servicing system. Required for FHA loans when investor reporting is enabled.',
    ),
    firstPaymentDate: DateString.and(dateIsWithinRange({ years: 30 })),
    initialEscrowBalance: MonetaryValue.optional(),
    initialInterestBalance: MonetaryValue.optional().describe(
      'Required when either condition is met:\ncurrentOutstandingAmount > 0\nOR\nfirstCollectedPaymentDate > firstPaymentDate',
    ),
    initialHoldbackBalance: MonetaryValue.optional(),
    initialLossDraftBalance: MonetaryValue.optional(),
    initialPrincipalPrepaymentBalance: NonNegativeMonetaryValue.optional(),
    initialInterestReserveBalance: NonNegativeMonetaryValue.optional(), // BUYDOWN TODO: Remove when finally deprecated
    initialMortgageInsuranceAmount: NonNegativeMonetaryValue.optional(),
    initialReserveBalance: NonNegativeMonetaryValue.optional(),
    initialStatementSuspenseBalance: NonNegativeMonetaryValue.optional().describe(
      'Will be automatically applied to generated statements. Cannot exceed the value of the first statement, instead the initial balances and UPB should be adjusted.',
    ),
    initialUnappliedSuspenseBalance: NonNegativeMonetaryValue.optional().describe(
      'Suspense that must be manually applied on the loan.',
    ),
    initialNonInterestBearingUnpaidPrincipal: NonNegativeMonetaryValue.optional().describe(
      'Deferred principal that is non-interest bearing and should be paid off prior to boarding.',
    ),
    initialOutstandingInterestOnEscrow: NonNegativeMonetaryValue.optional().describe(
      'A warning is returned when this is blank, interest on escrow is enabled, and the loan was funded before the billing period of the first payment Willow collects. Interest on escrow owed to the borrower and not yet credited, through the day before that billing period starts (for a first payment collected 5/15, through 4/14). Enter 0 if none.',
    ),
    buydownType: BuydownType.optional(),
    reserveBalanceFundedByThirdParty: Checkbox.optional(),
    isInterestReserve: Checkbox.optional(),
    isSubsidy: Checkbox.optional(),
    statementSubsidyAmount: NonNegativeMonetaryValue.optional().describe(
      'Amount of the monthly billed amount covered by the subsidy, subtracted from what the borrower is billed. Required when isSubsidy is true.',
    ),
    statementSubsidyEffectiveDate: DateString.optional().describe(
      'Date the statement subsidy amount takes effect. Defaults to the first payment date.',
    ),
    initialSubsidyBalance: NonNegativeMonetaryValue.optional(),
    isPendingActivation: Checkbox.optional().describe(
      'Boarding a loan as pending activation will prevent Willow from sending out communications to borrowers or accepting payments until the loan status is updated to active via the app or API',
    ),
    interestDueAtPayoffFlag: Checkbox.optional().describe(
      'If true, the loan is fully suspended: the borrower is billed $0 each month, interest still accrues and is collectible at payoff, and no delinquency, late fees, or late notices apply.',
    ),
    isDutch: Checkbox.optional(),

    // Draw fields
    drawsEnabled: Checkbox.optional(),
    maxLineOfCredit: NonNegativeMonetaryValue.optional(),
    drawTerm: NonNegativeInteger.optional().describe('Number of months draws are allowed'),
    drawExpirationDate: DateString.optional().describe('After this date, the ability to record a draw will be frozen'),
    repayBeginDate: DateString.optional().describe('The date when a loan starts amortizing'),
    draw1Date: DateString.optional().describe('Draw date prior to boarding in Willow.'),
    draw1Amount: NonNegativeMonetaryValue.optional().describe('Draw amount prior to boarding in Willow.'),
    draw2Date: DateString.optional().describe('Second draw date prior to boarding in Willow.'),
    draw2Amount: NonNegativeMonetaryValue.optional().describe('Second draw amount prior to boarding in Willow.'),
    draw3Date: DateString.optional().describe('Third draw date prior to boarding in Willow.'),
    draw3Amount: NonNegativeMonetaryValue.optional().describe('Third draw amount prior to boarding in Willow.'),

    // Entity fields
    entityCompanyId: z
      .string()
      .optional()
      .describe(
        'Required when entityName is present. (Provide entity fields if the loan belongs to an entity instead of, or in addition to, an individual.)',
      ),

    entityName: z
      .string()
      .optional()
      .describe(
        'Required when entityCompanyId is present. (Provide entity fields if the loan belongs to an entity instead of, or in addition to, an individual.)',
      ),
    entityEin: z
      .string()
      .optional()
      .describe(
        'Required when entityName or entityCompanyId is present. (Provide entity fields if the loan belongs to an entity instead of, or in addition to, an individual.)',
      ),
    entityPhone: OptionalPhoneNumber,
    entityEmail: OptionalEmailString,
    entityAddressLine1: AddressLine.optional(),
    entityAddressLine2: AddressLine.optional(),
    entityAddressLocality: z.string().optional(),
    entityAddressRegion: StateAbbreviation.optional().or(z.literal('')),
    entityAddressPostcode: Zipcode.optional(),
    entityAddressCountry: CountryCode.optional(),

    // escrow fields (flat escrow{n}* form; see EscrowAgentFields above)
    ...EscrowAgentFields.shape,
    monthlyPaymentMi: NonNegativeMonetaryValue.optional(), // TODO deprecate this from FF and the API
    beginningOfYearPrincipalBalance: NonNegativeMonetaryValue.optional().describe(
      'A loan that has been active since the beginning of the year should include this number for tax reporting. If it’s missing, the workflow to create a Form 1098 will flag it for review.',
    ),

    originationData: OriginationData.optional(),

    mipLTVThresholdDate: DateString.optional(),

    // forgiveness fields
    isForgiveable: Checkbox.optional().describe(
      'If true, the loan principal is periodically forgiven on a schedule. When true, forgivenessScheduleMonths, forgivenessReductionPercent, and forgivenessFirstDate are all required.',
    ),
    forgivenessScheduleMonths: NonNegativeInteger.optional().describe(
      'Number of months between each forgiveness event. Required when isForgiveable is true.',
    ),
    forgivenessReductionPercent: NonNegativePercentage.optional().describe(
      'Percentage of principal to forgive each period. Required when isForgiveable is true.',
    ),
    forgivenessFirstDate: DateString.optional().describe(
      'Date of the first forgiveness event. Required when isForgiveable is true.',
    ),

    // fee fields
    ...LoanFeeOverrideFields.shape,

    // NB spreading here avoids some "excessively deep and possibly infinite"
    // type instantiation errors. Zod's `.merge` does effectively the same
    // thing internally, but by spreading ourselves we avoid the redundant
    // evaluation of merged types that leads to the error.
    ...CreateLoanFields.shape,
    ...AddressFields.shape,
    ...CreateLoanMultipleAddressFields.shape,
  })
  .strict();
export type CreateLoanRow = z.infer<typeof CreateLoanRow>;

export const CREATE_LOAN_ROW_REQUIRED_FIELDS = Object.keys(CreateLoanRow.shape).filter(
  (key) => !CreateLoanRow.shape[key as keyof typeof CreateLoanRow.shape].isOptional(),
);
